import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import Indicate from '../../cards/Indicate';
import { AnalyticsData } from '../../../../pages/Analytics';
import useI18n from '../../../../hooks/useI18n';
import { translateChartLabels } from '../../../../utils/dateTimeTranslations';
import { formatCount } from '@/utils/numberUtils';

type ViewType = 'daily' | 'weekly' | 'monthly' | 'yearly';

const LinkViewChart: React.FC<{
  analyticsData: AnalyticsData;
  view: 'daily' | 'weekly' | 'monthly' | 'yearly';
  setView: Dispatch<SetStateAction<'daily' | 'weekly' | 'monthly' | 'yearly'>>;
}> = ({ view, setView, analyticsData }) => {
  const { t, currentLanguage } = useI18n();

  const viewOptions: { label: string; value: ViewType }[] = [
    { label: String(t('analytics.dates.day')).charAt(0), value: 'daily' },
    { label: String(t('analytics.dates.week')).charAt(0), value: 'weekly' },
    { label: String(t('analytics.dates.month')).charAt(0), value: 'monthly' },
    { label: String(t('analytics.dates.year')).charAt(0), value: 'yearly' },
  ];

  const timeFrameView = analyticsData.timeFrameView;
  const [series, setSeries] = useState([
    { name: String(t('analytics.clicks')), data: [] as number[] },
  ]);
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    const data = timeFrameView.map(({ count }) => count);
    const originalLabels = timeFrameView.map(({ _id }) => _id);

    // Traduire les labels en fonction de la langue et de la vue
    const translatedLabels = translateChartLabels(
      originalLabels,
      view,
      currentLanguage,
    );

    setSeries([{ name: String(t('analytics.clicks')), data }]);
    setCategories(translatedLabels);
  }, [timeFrameView, t, currentLanguage, view]);
  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'inherit',
        background: 'transparent',
        height: 350,
      },
      plotOptions: {
        bar: {
          columnWidth: 15,
          borderRadius: 7,
          dataLabels: {
            position: 'top',
          },
          distributed: false,
        },
      },
      responsive: [
        {
          breakpoint: 480,

          options: {
            chart: {
              width: '100%',
            },

            xaxis: {
              labels: {
                trim: false,
                style: {
                  fontSize: '10px',
                },
              },
            },
          },
        },
      ],
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.9,
          },
        },
        active: {
          filter: {
            type: 'none',
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories,
        labels: {
          style: {
            colors: '#383838',
            fontSize: '12px',
          },
        },
        axisBorder: {
          show: false,
        },
      },
      yaxis: {
        tickAmount: 6,
        labels: {
          style: {
            colors: '#9B919D',
            fontSize: '12px',
          },
          formatter: (value) => {
            if (value >= 1000) {
              return `${(value / 1000).toFixed(0)}k`;
            }
            return value.toString();
          },
        },
      },
      grid: {
        show: true,
        borderColor: '#EDEEEF',
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: false,
          },
        },
      },
      fill: {
        colors: ['#E3CCF4'],
        opacity: 1,
        type: 'solid',
        gradient: {
          shade: 'dark',
          type: 'horizontal',
          shadeIntensity: 0.5,
          opacityFrom: 1,
          opacityTo: 1,
        },
      },
      tooltip: {
        enabled: true,
        theme: 'light',
        style: {
          fontSize: '12px',
        },
        y: {
          formatter: (value) =>
            `${value.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${String(t('analytics.visits'))}`,
        },
      },
    }),
    [categories, view, t],
  );

  return (
    <div className="w-full bg-white px-3 py-4 rounded-3xl border border-[#E5E9EB]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-[#9B919D] text-xs mb-1">
            {String(t('analytics.userVisit'))}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-semibold text-[#292828]">
              {formatCount(analyticsData.totalClicked.count)}
            </span>
            <Indicate
              direction={
                analyticsData.totalClicked.changeInPercentage >= 0
                  ? 'up'
                  : 'down'
              }
              percent={
                analyticsData.totalClicked.changeInPercentage === 0
                  ? analyticsData.totalClicked.changeInPercentage
                  : +analyticsData.totalClicked.changeInPercentage.toFixed(2)
              }
            />
          </div>
        </div>
        <div className="flex gap-4 lg:gap-6">
          {viewOptions.map(({ label, value }) => (
            <button
              key={value}
              className={`text-sm transition-colors font-semibold ${view === value ? 'text-[#0561FC]' : 'text-[#C4CCD0]'}`}
              onClick={() => setView(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="h-[220px] max-w-[501px] ">
        <ReactApexChart
          options={options}
          series={series}
          type="bar"
          height="100%"
          width={501}
        />
      </div>
    </div>
  );
};

export default LinkViewChart;
